import { useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

interface CameraButtonProps {
  onPhoto: () => void;
  onVideoStart: () => void;
  onVideoStop: () => void;
  isRecording: boolean;
  disabled?: boolean;
  recordingProgress?: number; // 0-100
}

export const CameraButton = ({
  onPhoto,
  onVideoStart,
  onVideoStop,
  isRecording,
  disabled,
  recordingProgress = 0,
}: CameraButtonProps) => {
  const [isPressed, setIsPressed] = useState(false);
  const [longPressActivated, setLongPressActivated] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const vibrate = async (style: ImpactStyle = ImpactStyle.Light) => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style });
      } catch (error) {
        console.log('Haptic not available');
      }
    }
  };

  const handlePointerDown = () => {
    if (disabled) return;
    
    setIsPressed(true);
    setLongPressActivated(false);
    vibrate(ImpactStyle.Light);
    
    // Long press para vídeo (500ms igual Strava/Instagram)
    timerRef.current = setTimeout(() => {
      setLongPressActivated(true);
      vibrate(ImpactStyle.Medium);
      onVideoStart();
    }, 500);
  };

  const handlePointerUp = () => {
    if (disabled) return;
    
    setIsPressed(false);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isRecording) {
      vibrate(ImpactStyle.Heavy);
      onVideoStop();
    } else if (!longPressActivated) {
      vibrate(ImpactStyle.Medium);
      onPhoto();
    }
  };

  const handlePointerLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsPressed(false);
  };

  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20">
      {/* Texto de instrução acima do botão */}
      {!isRecording && (
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-max pointer-events-none">
          <p className="text-white text-sm font-medium text-center bg-black/60 backdrop-blur-md px-4 py-2 rounded-full">
            {isPressed && !longPressActivated 
              ? "⏺️ Segure para gravar..." 
              : "📸 Toque para foto"}
          </p>
        </div>
      )}

      {/* Contador de segundos durante gravação */}
      {isRecording && (
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="bg-red-500 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-white animate-pulse" />
            <span className="font-mono text-2xl font-bold">
              {Math.floor((recordingProgress / 100) * 15)}s
            </span>
          </div>
        </div>
      )}

      <button
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        disabled={disabled}
        className={cn(
          "relative w-20 h-20 rounded-full transition-all duration-200 active:scale-95",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        {/* Outer ring - progresso da gravação */}
        <svg
          className="absolute inset-0 w-full h-full -rotate-90"
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="white"
            strokeWidth="4"
            opacity="0.3"
          />
          {isRecording && (
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="#FF4444"
              strokeWidth="4"
              strokeDasharray={`${2 * Math.PI * 45}`}
              strokeDashoffset={`${2 * Math.PI * 45 * (1 - recordingProgress / 100)}`}
              className="transition-all duration-300"
            />
          )}
        </svg>

        {/* Inner button - Fica quadrado quando gravando */}
        <div
          className={cn(
            "absolute inset-3 transition-all duration-200",
            isRecording
              ? "bg-red-500 scale-50 rounded-md"
              : isPressed
              ? "bg-white scale-90 rounded-full"
              : "bg-white rounded-full"
          )}
        />
      </button>
    </div>
  );
};
